// project imports
import { enableMapSet } from 'immer';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import apiClient from 'utils/apiClient';
import type {RootState} from "./index";

enableMapSet();

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

interface AccountSummaryState {
    email: string
    isMatchEmail: boolean
    accounts: AccountProps[]
    selectedAccount?: AccountProps
    errorMessage: string
}

export const initialState: AccountSummaryState = {
    email: '',
    isMatchEmail: false,
    accounts: [],
    errorMessage: ''
};

interface AccountProps {
    uuid: string,
    name: string,
    status: string,
    primaryEmail: string,
    language: string,
    funnel: string,
    photoUrl: string,
    createdAt: string,
    updatedAt: string,
    activatedAt: string,
    lastSeen: number,
    timezone: string
}

export const searchAccountSummaries = createAsyncThunk<
    AccountProps[],
    string,
    { state: RootState, rejectValue: string }
>(
    'accountSummary/search',
    async (email: string, thunkAPI) => {
        const state = thunkAPI.getState();
        const { isMatchEmail } = state.accountSummary;

        if (!isMatchEmail) return thunkAPI.rejectWithValue('Invalid email.');

        try {
            const response = await apiClient.get<AccountProps[]>(`/search/accounts?email=${encodeURIComponent(email)}`);
            return response.data;
        } catch (err) {
            return thunkAPI.rejectWithValue((err as Error).message);
        }
    }
)

const accountSummarySlice = createSlice({
    name: "accountSummary",
    initialState,
    extraReducers: (builder) => {
        builder
            .addCase(searchAccountSummaries.pending, (state) => {
                state.errorMessage = '';
            })
            .addCase(searchAccountSummaries.fulfilled, (state, action) => {
                state.accounts = action.payload;
                if (state.accounts.length > 0) state.selectedAccount = state.accounts[0];

                if (state.accounts.length == 0) {
                    state.errorMessage = `${state.email} not found.`;
                }
            })
            .addCase(searchAccountSummaries.rejected, (state, action) => {
                state.errorMessage = action.payload || '';
            })
    },
    reducers: {
        setEmail: (state, action: PayloadAction<string>) => {
            state.email = action.payload;
            state.isMatchEmail = emailRegex.test(state.email);
            state.errorMessage = '';
        },
        setSelectedAccount: (state, action: PayloadAction<string>) => {
            state.selectedAccount = state.accounts
                .find(account => account.uuid === action.payload);
        }
    }
})

export const {
    setEmail,
    setSelectedAccount
} = accountSummarySlice.actions;
export default accountSummarySlice.reducer;
